import { OAC_STAGES } from '@/content/oac'

/**
 * The OpenAgents Control pipeline, as a diagram rather than a list of claims.
 *
 * Two SVGs — a horizontal rail above md, a vertical one below — both laid out
 * from OAC_STAGES so the breakpoints cannot drift apart as the copy changes.
 * Both are aria-hidden; the ordered list underneath is what a screen reader
 * gets, once, with the per-stage notes the desktop rail has no room for.
 *
 * There is no entrance animation on purpose. A draw-on reveal would mean an
 * observer, a reduced-motion branch and a second code path for about half a
 * second of delight, on a block whose job is to be skimmed.
 */

const STAGES = OAC_STAGES
/**
 * The one stage that is the person rather than the agent.
 *
 * The gate decorations — the split header rules and the loop-back arc — assume
 * a gate exists and has a stage before it, since `cx(GATE - 1)` is the arc's
 * target. A `findIndex` miss returns -1 and would place them at negative x with
 * nothing thrown, so they render only when there is a gate to draw. Clamping to
 * a fallback index would be worse: drop `gate: true` from `content/oac.ts` and
 * the page would quietly claim some other stage is the one you stand in.
 */
const GATE = STAGES.findIndex((stage) => 'gate' in stage)
const HAS_GATE = GATE > 0
const R = 19
/** Circle edge to arrowhead. Keeps the rail from touching the nodes. */
const CLEAR = R + 8

const num = (i: number) => String(i + 1).padStart(2, '0')

function HorizontalRail() {
  const w = 896
  const h = 175
  const railY = 52
  const col = w / STAGES.length
  const cx = (i: number) => col * i + col / 2

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      className="hidden h-auto w-full md:block"
      aria-hidden="true"
    >
      <defs>
        <marker
          id="oac-rail-arrow"
          viewBox="0 0 10 10"
          refX="8"
          refY="5"
          markerWidth="6"
          markerHeight="6"
          orient="auto-start-reverse"
        >
          <path
            d="M 0.5 1 L 8.5 5 L 0.5 9"
            fill="none"
            className="stroke-[#2e3833]"
            strokeWidth={1.6}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </marker>
        <marker
          id="oac-loop-arrow"
          viewBox="0 0 10 10"
          refX="8"
          refY="5"
          markerWidth="6"
          markerHeight="6"
          orient="auto-start-reverse"
        >
          <path
            d="M 0.5 1 L 8.5 5 L 0.5 9"
            fill="none"
            className="stroke-[#80cca5]"
            strokeWidth={1.6}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </marker>
      </defs>

      {/* The claim the section is really making: the gate splits the run in
          two, and nothing exists on disk on the left of it. */}
      {HAS_GATE && (
        <>
          <text
            x={(cx(0) + cx(GATE)) / 2}
            y={14}
            textAnchor="middle"
            className="fill-[#8b958f] font-mono text-[11px]"
          >
            nothing has been written yet
          </text>
          <text
            x={(cx(GATE + 1) + cx(STAGES.length - 1)) / 2}
            y={14}
            textAnchor="middle"
            className="fill-[#8b958f] font-mono text-[11px]"
          >
            changes land, one step at a time
          </text>
          <line
            x1={cx(0)}
            y1={24}
            x2={cx(GATE)}
            y2={24}
            className="stroke-[#232a26]"
            strokeWidth={1}
          />
          <line
            x1={cx(GATE + 1)}
            y1={24}
            x2={cx(STAGES.length - 1)}
            y2={24}
            className="stroke-[#232a26]"
            strokeWidth={1}
          />
        </>
      )}

      {STAGES.slice(0, -1).map((stage, i) => (
        <line
          key={`rail-${stage.name}`}
          x1={cx(i) + CLEAR}
          y1={railY}
          x2={cx(i + 1) - CLEAR}
          y2={railY}
          className="stroke-[#2e3833]"
          strokeWidth={2}
          markerEnd="url(#oac-rail-arrow)"
        />
      ))}

      {STAGES.map((stage, i) => {
        const gate = i === GATE
        return (
          <g key={stage.name}>
            <circle
              cx={cx(i)}
              cy={railY}
              r={R}
              className={
                gate
                  ? 'fill-[#80cca5]'
                  : 'fill-[#141817] stroke-[#2e3833] [stroke-width:1.5]'
              }
            />
            <text
              x={cx(i)}
              y={railY + 5}
              textAnchor="middle"
              className={
                gate
                  ? 'fill-[#0e1110] font-mono text-[12px] font-semibold'
                  : 'fill-[#80cca5] font-mono text-[12px]'
              }
            >
              {num(i)}
            </text>
            <text
              x={cx(i)}
              y={94}
              textAnchor="middle"
              className={`text-[15px] font-semibold ${gate ? 'fill-[#80cca5]' : 'fill-[#f0f3f1]'}`}
            >
              {stage.name}
            </text>
          </g>
        )
      })}

      {/* Declining at the gate rewrites the plan, not the repo. */}
      {HAS_GATE && (
        <>
          <path
            d={`M ${cx(GATE)} 108 C ${cx(GATE)} 148, ${cx(GATE - 1)} 148, ${cx(GATE - 1)} 108`}
            fill="none"
            className="stroke-[#80cca5] opacity-55"
            strokeWidth={1.5}
            strokeDasharray="4 4"
            markerEnd="url(#oac-loop-arrow)"
          />
          <text
            x={(cx(GATE) + cx(GATE - 1)) / 2}
            y={162}
            textAnchor="middle"
            className="fill-[#80cca5] font-mono text-[12px] opacity-85"
          >
            [n] — rewrite the plan
          </text>
        </>
      )}
    </svg>
  )
}

function VerticalRail() {
  const w = 358
  const top = 22
  const step = 80
  const cx = 24
  const h = top + step * (STAGES.length - 1) + R + 9
  const cy = (i: number) => top + step * i

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      className="h-auto w-full md:hidden"
      aria-hidden="true"
    >
      <defs>
        <marker
          id="oac-rail-arrow-v"
          viewBox="0 0 10 10"
          refX="8"
          refY="5"
          markerWidth="6"
          markerHeight="6"
          orient="auto-start-reverse"
        >
          <path
            d="M 0.5 1 L 8.5 5 L 0.5 9"
            fill="none"
            className="stroke-[#2e3833]"
            strokeWidth={1.6}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </marker>
      </defs>

      {STAGES.slice(0, -1).map((stage, i) => (
        <line
          key={`rail-${stage.name}`}
          x1={cx}
          y1={cy(i) + CLEAR}
          x2={cx}
          y2={cy(i + 1) - CLEAR}
          className="stroke-[#2e3833]"
          strokeWidth={2}
          markerEnd="url(#oac-rail-arrow-v)"
        />
      ))}

      {STAGES.map((stage, i) => {
        const gate = i === GATE
        return (
          <g key={stage.name}>
            <circle
              cx={cx}
              cy={cy(i)}
              r={R}
              className={
                gate
                  ? 'fill-[#80cca5]'
                  : 'fill-[#141817] stroke-[#2e3833] [stroke-width:1.5]'
              }
            />
            <text
              x={cx}
              y={cy(i) + 5}
              textAnchor="middle"
              className={
                gate
                  ? 'fill-[#0e1110] font-mono text-[12px] font-semibold'
                  : 'fill-[#80cca5] font-mono text-[12px]'
              }
            >
              {num(i)}
            </text>
            <text
              x={58}
              y={cy(i) - 3}
              className={`text-[16px] font-semibold ${gate ? 'fill-[#80cca5]' : 'fill-[#f0f3f1]'}`}
            >
              {stage.name}
            </text>
            <text x={58} y={cy(i) + 15} className="fill-[#8b958f] text-[13px]">
              {stage.note}
            </text>
          </g>
        )
      })}
    </svg>
  )
}

export function WorkflowDiagram() {
  return (
    <div className="mb-8">
      <HorizontalRail />
      <VerticalRail />

      {/* The loop-back arc has nowhere to go at phone width, so below md it
          becomes the sentence it was drawing. Named from the stage rather than
          numbered in prose, so reordering cannot leave the phone copy pointing
          at one stage while the desktop arc points at another. */}
      {HAS_GATE && (
        <p className="mt-5 border-l-2 border-[#80cca5] pl-4 leading-relaxed text-[#c3ccc7] md:hidden">
          Say no at the {STAGES[GATE].name} stage and the plan gets rewritten,
          not your repo.
        </p>
      )}

      <ol className="sr-only">
        {STAGES.map((stage) => (
          <li key={stage.name}>
            {stage.name} — {stage.note}
          </li>
        ))}
      </ol>
    </div>
  )
}
